import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Activity,
    Phone,
    Home,
    User,
    PlayCircle,
    Clock,
    CheckCircle,
    Heart
} from 'lucide-react';
import { generateWhatsAppLink } from '../mock/mockData';
import { useAuth } from '../context/AuthContext';

const Physiotherapy = () => {
    const { user } = useAuth();
    const isAuthenticated = !!user;

    const handleBookSession = (serviceName) => {
        const whatsAppLink = generateWhatsAppLink('physio', {
            user: user,
            service: serviceName,
            type: 'Home Visit'
        });
        window.open(whatsAppLink, '_blank');
    };

    const exerciseVideos = [
        {
            title: "Knee Pain Relief",
            duration: "15 mins",
            thumbnail: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw1fHxwaHlzaW90aGVyYXB5fGVufDB8fHx8MTc1ODgxOTkzMHww&ixlib=rb-4.1.0&q=85",
            target: "Seniors"
        },
        {
            title: "Back Strengthening",
            duration: "20 mins",
            thumbnail: "https://images.unsplash.com/photo-1518611012118-696072aa579a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw4fHxleGVyY2lzZXxlbnwwfHx8fDE3NTg4MTk5MzB8MA&ixlib=rb-4.1.0&q=85",
            target: "All Ages"
        },
        {
            title: "Neck Mobility",
            duration: "10 mins",
            thumbnail: "https://images.unsplash.com/photo-1544367563-12123d8966cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHx5b2dhfGVufDB8fHx8MTc1ODgxOTkzMHww&ixlib=rb-4.1.0&q=85",
            target: "Seniors"
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-flex items-center space-x-2 bg-teal-500/30 px-4 py-2 rounded-full mb-6">
                                <Home className="w-5 h-5 text-teal-100" />
                                <span className="font-medium text-teal-50">Home Healthcare Services</span>
                            </div>
                            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                                Expert Physiotherapy <br />
                                <span className="text-teal-200">at Your Doorstep</span>
                            </h1>
                            <p className="text-xl text-teal-50 mb-8 max-w-lg">
                                Recover faster in the comfort of your home. Specialized care for seniors, post-surgery rehabilitation, and chronic pain management.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Button
                                    onClick={() => handleBookSession('General Consultation')}
                                    className="bg-white text-teal-700 hover:bg-teal-50 text-lg px-8 py-6 h-auto font-semibold shadow-lg"
                                >
                                    <Phone className="w-5 h-5 mr-3" />
                                    Book Home Visit
                                </Button>
                                <div className="flex items-center space-x-2 text-teal-100 px-4">
                                    <Activity className="w-5 h-5" />
                                    <span>Certified Therapists</span>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxwaHlzaW90aGVyYXBpc3R8ZW58MHx8fHwxNzU4ODE5OTMwfDA&ixlib=rb-4.1.0&q=85"
                                alt="Physiotherapy Session"
                                className="w-full h-[450px] object-cover rounded-2xl shadow-2xl"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl hidden md:block">
                                <div className="flex items-center space-x-4">
                                    <div className="bg-teal-100 p-3 rounded-full">
                                        <User className="w-8 h-8 text-teal-600" />
                                    </div>
                                    <div>
                                        <p className="text-gray-500 text-sm">Satisfied Patients</p>
                                        <p className="text-2xl font-bold text-gray-900">5,000+</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Comprehensive Care</h2>
                        <p className="text-gray-600 text-lg">Specialized treatments tailored to your needs</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                title: "Geriatric Care",
                                desc: "Specialized mobility and strength training for seniors to improve independence.",
                                icon: Heart
                            },
                            {
                                title: "Post-Surgery Rehab",
                                desc: "Structured recovery programs for joint replacements and fractures.",
                                icon: Activity
                            },
                            {
                                title: "Pain Management",
                                desc: "Advanced techniques for back pain, neck pain, and arthritis relief.",
                                icon: CheckCircle
                            }
                        ].map((service, idx) => (
                            <Card key={idx} className="hover:shadow-lg transition-shadow border-teal-100">
                                <CardContent className="p-8 text-center">
                                    <div className="bg-teal-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <service.icon className="w-8 h-8 text-teal-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                    <p className="text-gray-600">{service.desc}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Exercises Section */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between mb-12">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">Home Exercises</h2>
                            <p className="text-gray-600">Guided routines for daily wellness</p>
                        </div>
                        {isAuthenticated && (
                            <Badge className="bg-orange-100 text-orange-700 px-4 py-2 text-sm">
                                Subscriber Exclusive
                            </Badge>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {exerciseVideos.map((video, idx) => (
                            <div key={idx} className="group cursor-pointer">
                                <div className="relative mb-4 overflow-hidden rounded-xl">
                                    <img
                                        src={video.thumbnail}
                                        alt={video.title}
                                        className="w-full h-56 object-cover transform group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                                        <PlayCircle className="w-16 h-16 text-white opacity-90 group-hover:scale-110 transition-transform" />
                                    </div>
                                    <div className="absolute bottom-4 right-4 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium flex items-center">
                                        <Clock className="w-3 h-3 mr-1" />
                                        {video.duration}
                                    </div>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">
                                    {video.title}
                                </h3>
                                <div className="flex items-center space-x-2">
                                    <Badge variant="outline" className="text-gray-600 border-gray-300">
                                        {video.target}
                                    </Badge>
                                </div>
                            </div>
                        ))}
                    </div>

                    {!isAuthenticated && (
                        <div className="mt-12 bg-orange-50 rounded-2xl p-8 text-center">
                            <h3 className="text-xl font-bold text-gray-900 mb-2"> Unlock Full Library</h3>
                            <p className="text-gray-600 mb-6">Subscribe to access 100+ premium exercise videos for seniors.</p>
                            <Button className="bg-orange-600 hover:bg-orange-700">
                                View Subscription Plans
                            </Button>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Physiotherapy;
